import { IUser } from '@my-backoffice/app/pages/user/shared/models/interfaces/user.interface';
import { SERVER_URL } from '@my-backoffice/app/shared/constants/app.constants';
import { ECookieKeys } from '@my-backoffice/app/shared/models/enums/cookie-keys.enum';
import { EStorageKeys } from '@my-backoffice/app/shared/models/enums/storage-keys.enum';
import { IAuthenticationData } from '@my-backoffice/app/shared/models/interfaces/authentication-data.interface';
import { HttpService } from '@my-backoffice/app/shared/services/http-services/http.service';
import { AppOverlayService } from '@my-backoffice/app/shared/services/modal-services/app-overlay/app-overlay.service';
import { CookieStorageService } from '@my-backoffice/app/shared/services/storage-services/cookie-storage/cookie-storage.service';
import { StorageService } from '@my-backoffice/app/shared/services/storage-services/storage.service';
import { WINDOW } from '@my-backoffice/app/shared/tokens/window.token';
import { ENVIRONMENT } from '@my-backoffice/environments/environment';
import { Inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEmpty } from 'lodash/index';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

declare let ENV_TYPE: 'dev' | 'pp' | 'prod';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AppAuthenticationService {
  public readonly currentUserSubject$: Subject<IUser> = new Subject<IUser>();

  public constructor(
    private readonly _cookieStorage: CookieStorageService,
    private readonly _storageService: StorageService,
    private readonly _httpService: HttpService,
    private readonly _overlayService: AppOverlayService,
    @Inject(WINDOW) private readonly _window: Window
  ) {}

  public updateUserData(data: Readonly<IUser>): void {
    this.currentUserSubject$.next(data);
  }

  public navigateToLogin(): void {
    const redirectUrl = encodeURIComponent(`${location.pathname + location.search}`);

    location.assign(`/v2/login?redirect=${redirectUrl}`);
  }

  public saveAuthenticationData(authData: IAuthenticationData): void {
    const expires = /^\d+$/.test(authData.validBefore) ? new Date(+authData.validBefore * 1000) : undefined;

    this._cookieStorage.set(ECookieKeys.AUTH_TOKEN, authData.token, { expires });
    this._cookieStorage.set(ECookieKeys.USER_ID, authData.userId, { expires });
    this._cookieStorage.set(ECookieKeys.USER_NAME, authData.userName, { expires });
    this._cookieStorage.set(ECookieKeys.VALID_BEFORE, authData.validBefore, { expires });
    this._cookieStorage.set(ECookieKeys.SESSION_ID, authData.sessionId, { expires });
    this._cookieStorage.set(ECookieKeys.LOGIN_TYPE, authData.loginType, { expires });
    this._cookieStorage.set(ECookieKeys.REFRESH_KEY, authData.refreshKey, { expires });
    this._cookieStorage.set(ECookieKeys.USER_TYPE, authData.userType, { expires });
    // TODO: remove user id from session storage
    this._storageService.setItem(EStorageKeys.USER_ID, authData.userId);
  }

  /**
   * get authentication data from cookie, not session storage!
   * @returns {Readonly<Partial<IAuthenticationData>>} Authentication data from cookies.
   */
  public getAuthenticationData(): Readonly<Partial<IAuthenticationData>> {
    return {
      token: this._cookieStorage.get(ECookieKeys.AUTH_TOKEN),
      userId: this._cookieStorage.get(ECookieKeys.USER_ID),
      userName: this._cookieStorage.get(ECookieKeys.USER_NAME),
      validBefore: this._cookieStorage.get(ECookieKeys.VALID_BEFORE),
      sessionId: this._cookieStorage.get(ECookieKeys.SESSION_ID),
      loginType: this._cookieStorage.get(ECookieKeys.LOGIN_TYPE),
      refreshKey: this._cookieStorage.get(ECookieKeys.REFRESH_KEY),
      userType: this._cookieStorage.get(ECookieKeys.USER_TYPE),
    };
  }

  public clearAuthenticationData(): void {
    this._storageService.clearAuthenticationData();
    this._cookieStorage.remove(ECookieKeys.AUTH_TOKEN);
    this._cookieStorage.remove(ECookieKeys.USER_ID);
    this._cookieStorage.remove(ECookieKeys.USER_NAME);
    this._cookieStorage.remove(ECookieKeys.VALID_BEFORE);
    this._cookieStorage.remove(ECookieKeys.SESSION_ID);
    this._cookieStorage.remove(ECookieKeys.LOGIN_TYPE);
    this._cookieStorage.remove(ECookieKeys.REFRESH_KEY);
    this._cookieStorage.remove(ECookieKeys.USER_TYPE);
  }

  public logout(): void {
    this._overlayService.updateIsLoading(true);
    this.deleteToken$()
      .pipe(untilDestroyed(this))
      .subscribe((): void => {
        this.clearAuthenticationData();
        this._window.location.replace(`${ENVIRONMENT[ENV_TYPE].MARKETING_WEBSITE_URL}/logout`);
      });
  }

  public isAuthenticated(): boolean {
    return !isEmpty(this._cookieStorage.get(ECookieKeys.AUTH_TOKEN));
  }

  public isTokenExpired(): boolean {
    const validBefore: string = this._cookieStorage.get(ECookieKeys.VALID_BEFORE);

    if (!validBefore) {
      return false;
    }

    const now = Date.now() / 1000;

    return now >= parseInt(validBefore, 10);
  }

  public isValidSession(): boolean {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);
    const token = this._cookieStorage.get(ECookieKeys.AUTH_TOKEN);

    return !!userId && !!token && !this.isTokenExpired();
  }

  public getCurrentUser(): IUser {
    return JSON.parse(this._storageService.getItem(EStorageKeys.USER_DATA) || '{}');
  }

  public deleteToken$(): Observable<void> {
    return this._httpService.httpClient
      .delete<void>(`${SERVER_URL}/auth/v2/token`, {
        observe: 'body',
      })
      .pipe(
        map((response: void): void => response),
        catchError((): Observable<void> => {
          return of<void>(undefined);
        })
      );
  }
}
